import { Link } from "react-router-dom";
import HumanoidIntelligenceShell, { MetricCard } from "../../components/humanoid/HumanoidIntelligenceShell";
import {
  chinaHumanoidModels,
  chinaOrderLedger,
  documentedDeployments,
  documentedFailureEvidence,
  fundingEvidence,
  humanoidIntelligenceLinks,
  chinaComponentSuppliers,
} from "../../data/humanoidIntelligence";
import { humanoidCatalogStats } from "../../data/humanoidCatalog";

export default function HumanoidIntelligenceHub() {
  const descriptions = {
    "/humanoid-robot-company-growth": "Documented company founding years; incomplete coverage and dates still awaiting evidence are identified.",
    "/china-humanoid-order-tracker": "Chinese commercial orders with a stated quantity or contract; no qualifying rows are currently listed.",
    "/robotics/humanoid-robot-company-funding": "A source-linked record of reported financing rounds.",
    "/humanoid-robot-deployment-map": "Customer locations and commercial agreements, with repeated project milestones identified.",
    "/humanoid-robot-failures": "AgiBot validation claims and interaction-data categories; not an industry incident register.",
    "/china-humanoid-component-supply-chain": "One listed supplier; this does not represent the wider Chinese component market.",
  };
  return (
    <HumanoidIntelligenceShell
      title="Humanoid Robotics Data Index"
      description="Find humanoid model comparisons, company records, deployment evidence and funding sources, with the coverage limits of each collection made explicit."
      path="/humanoid-intelligence"
      eyebrow="HUMANOID RESEARCH NAVIGATION"
      modifiedTime="2026-09-29"
      reviewLabel="Index reviewed September 29, 2026; source dates vary by record"
      schemaType="CollectionPage"
    >
      <section className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard value={humanoidCatalogStats.robotCount} label="Robots tracked" detail={`${humanoidCatalogStats.manufacturerCount} manufacturers in the retained humanoid catalog`} />
        <MetricCard value={chinaHumanoidModels.length} label="Chinese models" detail="Catalogued models; consult individual sources and dates" />
        <MetricCard value={documentedDeployments.length} label="Deployment-related records" detail="Includes agreements and repeated milestones" />
        <MetricCard value={documentedFailureEvidence.length} label="AgiBot evidence rows" detail="Validation claims and dataset categories" />
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {humanoidIntelligenceLinks.map((item) => (
          <article key={item.path} className="border border-white/10 bg-[#07101f]/85 p-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl font-black tracking-tight text-white">{item.path === "/humanoid-robot-company-growth" ? "Company founding records" : item.path === "/humanoid-robot-deployment-map" ? "Deployment locations and agreements" : item.path === "/humanoid-robot-failures" ? "AgiBot failure evidence" : item.label}</h2>

            </div>
            <p className="mt-3 text-sm leading-7 text-slate-400">{descriptions[item.path] || item.description}</p>
            <Link to={item.path} className="mt-5 inline-flex text-sm font-black text-cyan-300 hover:text-cyan-100">Open collection →</Link>
          </article>
        ))}
      </section>

      <section className="mt-8 grid gap-4 lg:grid-cols-2">
        <article className="border border-white/10 bg-white/[0.035] p-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">DATA INTEGRITY</p>
          <h2 className="mt-3 text-2xl font-black text-white">Distinctions to check in each source</h2>
          <div className="mt-5 grid grid-cols-2 gap-px bg-white/10 text-sm">
            {["Orders ≠ shipments", "Shipments ≠ production", "Production ≠ deployments", "Demo ≠ deployment", "Pilot ≠ production deployment", "Funding ≠ valuation", "Contract value ≠ revenue", "Teleoperation ≠ autonomous task"].map((item) => (
              <div key={item} className="bg-[#07101f] p-3 font-bold text-slate-300">{item}</div>
            ))}
          </div>
        </article>
        <article className="border border-white/10 bg-white/[0.035] p-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-300">CURRENT COVERAGE</p>
          <h2 className="mt-3 text-2xl font-black text-white">Coverage remains uneven</h2>
          <p className="mt-4 text-sm leading-7 text-slate-400">The order collection currently contains {chinaOrderLedger.length} qualifying Chinese commercial order rows. The component collection lists {chinaComponentSuppliers.length} supplier; it cannot support a representative market comparison.</p>
          <p className="mt-4 text-sm leading-7 text-slate-400">The funding collection contains {fundingEvidence.length} reported financing records. Record counts describe this collection, not the size of the market.</p>
        </article>
      </section>


    </HumanoidIntelligenceShell>
  );
}
